import React from 'react'
import moment from 'moment'
import {
  ActionButtons,
  DataTable,
  DataTableColumn,
  EmptyState,
} from '@enamelai/design-system'
import { Icon as CalendarIcon } from '@enamelai/design-system/icons/Scheduler'
import { TableSkeleton } from '@components/ui'
import { AppointmentStatus } from '@dataTypes/appointment.type'
import type { AiReceptionistAppointmentRow } from '@services/api/ai-receptionist-settings.api'
import { AppointmentStatusMap } from '@utils/appointment.util'
import AppointmentStatusBadgeSelect from './AppointmentStatusBadgeSelect.component'

export const formatAppointmentDate = (value?: string | null): string => {
  if (!value || value === '—') return '—'
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format('MMM DD, YYYY') : value
}

export const formatAppointmentTimeRange = (
  start?: string | null,
  end?: string | null,
): string => {
  const formatTime = (value?: string | null): string => {
    if (!value || value === '—') return ''
    const parsed = moment(value, ['HH:mm:ss', 'HH:mm', 'h:mm A'], true)
    if (parsed.isValid()) return parsed.format('hh:mm A')
    const loose = moment(value)
    return loose.isValid() ? loose.format('hh:mm A') : value
  }
  const startLabel = formatTime(start)
  const endLabel = formatTime(end)
  if (startLabel && endLabel) return `${startLabel} - ${endLabel}`
  return startLabel || endLabel || '—'
}

export type AiReceptionistAppointmentsTabProps = {
  loading: boolean
  appointments: AiReceptionistAppointmentRow[]
  onView: (appointment: AiReceptionistAppointmentRow) => void
  onEdit: (appointment: AiReceptionistAppointmentRow) => void
  onDelete: (appointment: AiReceptionistAppointmentRow) => void
  onStatusChange: (
    appointment: AiReceptionistAppointmentRow,
    status: AppointmentStatus,
  ) => void
  emptyDescription?: string
}

const AiReceptionistAppointmentsTab = ({
  loading,
  appointments,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  emptyDescription,
}: AiReceptionistAppointmentsTabProps): React.ReactElement => {
  const renderActions = (
    row: AiReceptionistAppointmentRow,
  ): React.ReactElement => (
    <ActionButtons
      onView={() => onView(row)}
      onEdit={() => onEdit(row)}
      onDelete={() => onDelete(row)}
    />
  )

  const columns: DataTableColumn<AiReceptionistAppointmentRow>[] = [
    { id: 'patient_name', label: 'Patient name', sortable: true },
    { id: 'provider_name', label: 'Provider', sortable: true },
    { id: '_date', label: 'Date', sortable: true },
    { id: '_time', label: 'Time', sortable: true },
    { id: 'subcategory', label: 'Service', sortable: true },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      renderCell: (row) => {
        const statusCode = row.appt_status
        if (typeof statusCode !== 'number') {
          return String(row.status ?? '—')
        }
        const status = statusCode as AppointmentStatus
        if (!(status in AppointmentStatusMap)) {
          return String(row.status ?? '—')
        }
        return (
          <AppointmentStatusBadgeSelect
            status={status}
            onChange={(nextStatus) =>
              onStatusChange(row as AiReceptionistAppointmentRow, nextStatus)
            }
          />
        )
      },
    },
  ]

  if (loading) {
    return (
      <TableSkeleton
        className="ai-receptionist-appointments__table"
        columns={[
          { width: '18%', kind: 'text' },
          { width: '16%', kind: 'text' },
          { width: '14%', kind: 'text' },
          { width: '16%', kind: 'text' },
          { width: '12%', kind: 'badge' },
          { width: '120px', kind: 'actions' },
        ]}
        rows={3}
        showToolbar={false}
        showPagination={false}
        actionCount={3}
        fillHeight={false}
      />
    )
  }

  if (appointments.length === 0) {
    return (
      <EmptyState
        className="ai-receptionist-appointments__empty"
        illustration={<CalendarIcon className="empty-icon" aria-hidden />}
        title="No appointments found"
        description={
          emptyDescription ??
          'No appointments have been created or updated by the AI receptionist yet.'
        }
      />
    )
  }

  return (
    <DataTable
      className="ai-receptionist-appointments__table"
      columns={columns}
      rows={appointments.map((appointment) => ({
        _date: formatAppointmentDate(String(appointment.date ?? '')),
        _time: formatAppointmentTimeRange(
          String(appointment.time ?? ''),
          String(appointment.time_end ?? ''),
        ),
        ...appointment,
      }))}
      renderActions={renderActions}
    />
  )
}

export default AiReceptionistAppointmentsTab
